import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Modal,
  Input,
  DatePicker,
  TimePicker,
  Space,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import axios from 'axios'
import 'antd/dist/reset.css'
import './MasterCSS.css'
import {
  CreateEventMaster,
  DeleteEventMaster,
  GetEventMaster,
  UpdateEventMaster,
} from '../../../AllAPI/AdminAPI'
import { toast } from 'react-toastify'

const EventMaster = () => {
  const BranchId = sessionStorage.getItem('selected_branch')
  const BoardingYear = sessionStorage.getItem('BoardingYear')

  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [eventName, setEventName] = useState('')
  const [eventDate, setEventDate] = useState(null)
  const [eventTime, setEventTime] = useState(null)
  const [editingKey, setEditingKey] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  const [searchText, setSearchText] = useState('')

  // ---------------------------------------------
  // 🚀 Fetch Event List API
  // ---------------------------------------------
  const fetchEvents = async () => {
    try {
      setLoading(true)
      const res = await axios.get(`${GetEventMaster}/${BranchId}/${BoardingYear}`)
      const apiData = res.data.map((item) => ({
        key: item.id,
        event_name: item.event_name,
        event_date: item.event_date,
        event_time: item.event_time,
      }))
      setData(apiData)
    } catch (error) {
      toast.error('Failed to load events')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  // ---------------------------------------------
  // OPEN MODAL FOR ADD / EDIT
  // ---------------------------------------------
  const handleOpenModal = (record = null) => {
    if (record) {
      setEditingKey(record.key)
      setEventName(record.event_name)
      setEventDate(dayjs(record.event_date))
      setEventTime(dayjs(record.event_time, 'hh:mm A'))
    } else {
      setEditingKey(null)
      setEventName('')
      setEventDate(null)
      setEventTime(null)
    }
    setIsModalOpen(true)
  }

  const handleCancel = () => {
    setIsModalOpen(false)
    setEventName('')
    setEventDate(null)
    setEventTime(null)
    setEditingKey(null)
  }

  // ---------------------------------------------
  // SAVE (ADD / UPDATE)
  // ---------------------------------------------
  const handleSave = async () => {
    if (!eventName.trim() || !eventDate || !eventTime) {
      toast.warning('Please fill all fields')
      return
    }

    const payload = {
      branch_id:parseInt(BranchId),
      event_name:eventName,
      event_date: eventDate.format('YYYY-MM-DD'),
      event_time: eventTime.format('hh:mm A'),
      year:BoardingYear,
    }

    try {
      if (editingKey) {
        const res = await axios.put(`${UpdateEventMaster}/${editingKey}`, payload)
         if (res.data == "UPDATED") {          
          toast.success('Event updated successfully')
          fetchEvents()
          handleCancel()
        } else{
          toast.warn("Something Went Wrong! Please try again")
        }
      } else {
        const res = await axios.post(CreateEventMaster, payload)
        if (res.data == "SAVED") {
          toast.success('Event added successfully')
          fetchEvents()
          handleCancel()          
        }
        else{
          toast.warn("Something Went Wrong! Please try again")
        }
      }

    } catch (error) {
      toast.error('Failed to save event')
    }
  }

  // ---------------------------------------------
  // DELETE EVENT
  // ---------------------------------------------
  const handleDelete = async (key) => {
    try {
      const res = await axios.delete(`${DeleteEventMaster}/${key}`)

       if (res.data == "DELETED") {
          toast.success('Event deleted successfully')
          fetchEvents()
          handleCancel()          
        }
    } catch (error) {
      toast.error('Failed to delete event')
    }
  }

  const openDeleteModal = (id) => {
    setDeleteId(id)
    setIsDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    await handleDelete(deleteId)
    setIsDeleteModalOpen(false)
    setDeleteId(null)
  }

  // ---------------------------------------------
  // FILTER
  // ---------------------------------------------
   const filteredData = data.filter((item) => {
      const search = searchText.toLowerCase();
       const dateString = item.event_date
    ? new Date(item.event_date).toLocaleDateString("en-GB") // DD/MM/YYYY
    : "";

      return (
         dateString.toLowerCase().includes(search) ||
          (item.event_name ?? '').toLowerCase().includes(search)
      );
    });

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'event_name',
    },
    {
      title: 'Date',
      dataIndex: 'event_date',
      align: 'center',
    },
    {
      title: 'Time',
      dataIndex: 'event_time',
      align: 'center',
    },
    {
      title: 'Actions',
      align: 'center',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            size="small"
            onClick={() => openDeleteModal(record.key)}
          />
        </Space>
      ),
    },
  ]

  return (
    <div className="form-container mainContainer">
      <div className="row p-0 m-0 pb-5">
        <div className="col-12 col-md-2"></div>
        <div className="col-12 col-md-8">
          <div className="header-row">
            <h2 className="page-title">Event Master</h2>
          </div>

          <div className="search-bar">
            <Input
              placeholder="Search event..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 250 }}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenModal()}
              style={{ marginLeft: '10px' }}
            >
              Add Event
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={filteredData}
            pagination={{ pageSize: 10 }}
            loading={loading}
            bordered
            rowKey="key"
          />
        </div>
        <div className="col-12 col-md-2"></div>
      </div>

      {/* ===SAVE MODAL==== */}
      <Modal
        title={editingKey ? 'Edit Event' : 'Add Event'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="save" type="primary" onClick={handleSave}>
            Save
          </Button>,
        ]}
      >
        <Input
          size="large"
          placeholder="Enter event name"
          value={eventName}
          onChange={(e) => setEventName(e.target.value)}
          style={{ marginBottom: '10px' }}
        />

        <DatePicker
          size="large"
          placeholder="Select event date"
          value={eventDate}
          onChange={setEventDate}
          format="DD-MM-YYYY"
          style={{ width: '100%', marginBottom: '10px' }}
        />

        <TimePicker
          use12Hours
          format="hh:mm A"
          size="large"
          placeholder="Select event time"
          value={eventTime}
          onChange={setEventTime}
          style={{ width: '100%', marginBottom: '10px' }}
        />


      </Modal>

      {/* ===DELETE MODAL==== */}
      <Modal
        title="Confirm Delete"
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsDeleteModalOpen(false)}>
            Cancel
          </Button>,
          <Button key="delete" danger type="primary" onClick={confirmDelete}>
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to delete this Event?</p>
      </Modal>
    </div>
  )
}

export default EventMaster
